import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { CorePage } from 'ui/base/page/core_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { SearchInputStore } from 'ui/standard/input/search_input/search_input_store';
import { PangolinSelector } from 'ui/standard/pangolin_selector/pangolin_selector';
import { DeviceSearch } from 'ui/standard/search/device_search/device_search';

import { Redirect } from 'pages/core/redirect/redirect';

import { BasePath } from 'services/base/page_path';
import { togglePullToRefresh } from 'services/base/ipc';
import { GlobalSettingsStore } from 'services/base/app_settings';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain, HostMap } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { countConnectedDevices } from 'services/pangolin/hosts_service';

import { NetworkStatus } from './ui/network_status/network_status';
import { DeviceDisplay } from './ui/device_display/device_display';

import baseStyles from './list_devices.module.css';

type ListDevicesStyles = {
  container: string;
  header: string;
  headerItem: string;
  content: string;
  pangolinName: string;
};

const themedStyles: ThemedStyles<ListDevicesStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    header: baseStyles.daylightHeader,
    headerItem: baseStyles.headerItem,
    content: baseStyles.content,
    pangolinName: baseStyles.pangolinName,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    header: baseStyles.midnightHeader,
    headerItem: baseStyles.headerItem,
    content: baseStyles.content,
    pangolinName: baseStyles.pangolinName,
  },
};

const filterHosts = (hosts: HostMap) => {
  if (GlobalSettingsStore.get().hideOfflineDevices) {
    return hosts.filter((host) => host.isOnline);
  }
  return hosts;
};

type BaseListDevicesProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseListDevices = observer(({ pangolin, domain }: BaseListDevicesProps) => {
  const styles = useStyles(themedStyles);
  const [search] = React.useState(new SearchInputStore());

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLElement>) => {
    togglePullToRefresh(event.currentTarget.scrollTop === 0);
  }, []);

  React.useEffect(() => {
    togglePullToRefresh(true);
    return () => {
      togglePullToRefresh(false);
    };
  }, []);

  const filteredHosts = filterHosts(domain.hosts);

  return (
    <CorePage pangolin={pangolin} location={BasePath.DEVICES_LIST}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerItem}>
            <PangolinSelector pangolin={pangolin} location={BasePath.DEVICES_LIST}>
              <div className={styles.pangolinName}>
                {pangolin.nickname}
              </div>
            </PangolinSelector>
            <DeviceSearch
              store={search}
              pangolin={pangolin}
              hosts={filteredHosts}
            />
          </div>
          <div className={styles.headerItem}>
            <NetworkStatus
              pangolin={pangolin}
              deviceCount={countConnectedDevices(domain.hosts)}
            />
          </div>
        </div>
        <div className={styles.content} onScroll={handleScroll}>
          {
            filteredHosts.map((host) => (
              <DeviceDisplay
                host={host}
                pangolin={pangolin}
                key={host.id}
              />
            ))
          }
        </div>
      </div>
    </CorePage>
  );
});

export const ListDevices = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseListDevices
      pangolin={pangolin}
      domain={domain}
    />
  );
});
